<div class="min-h-screen bg-gradient">
  <div class="container">
    <div class="card-wrapper">
      <!-- Step 1: Phone Number Entry -->
      <div *ngIf="currentStep === 'phone'" class="card">
        <div class="card-header text-center">
          <!-- Skeleton Loader -->
          <div *ngIf="loadingBusiness" class="skeleton-header">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-title"></div>
          </div>

          <!-- Actual Content -->
          <ng-container *ngIf="!loadingBusiness">
            <div class="icon-wrapper">
              <img
                class="logo rounded-circle"
                [src]="businessData?.storeLogo"
                alt="Business Logo"
                (error)="$event.target.src = 'assets/icons/logo.svg'"
              />
            </div>
            <h2 class="card-title">
              Check In at {{ businessData?.storeName || "Store" }}
            </h2>
          </ng-container>
        </div>

        <div class="card-content">
          <!-- Error Message -->
          <div *ngIf="error" class="alert alert-danger">
            {{ error }}
          </div>

          <form [formGroup]="phoneForm" (ngSubmit)="onPhoneSubmit()">
            <div class="form-group">
              <label for="phone">Phone Number</label>
              <input
                type="text"
                id="phone"
                class="form-control"
                formControlName="phoneNumber"
                placeholder="(555) 123-4567"
                (input)="formatPhoneNumber($event)"
                maxlength="14"
                [disabled]="loading"
              />
              <small class="form-text text-muted">Format: (123) 456-7890</small>
            </div>

            <button
              type="submit"
              class="btn btn-primary btn-block"
              [disabled]="loading || phoneForm.invalid"
            >
              <span *ngIf="!loading">Continue</span>
              <span *ngIf="loading" class="loading-content">
                <span class="spinner"></span>
                Checking...
              </span>
            </button>
          </form>
        </div>
      </div>

      <!-- Step 2: OTP Verification -->
      <div *ngIf="currentStep === 'otp'" class="card">
        <div class="card-header text-center">
          <div class="icon-wrapper">
            <i class="material-icons" style="font-size: 48px; color: white"
              >smartphone</i
            >
          </div>
          <h2 class="card-title">Enter Verification Code</h2>
          <p class="card-description">
            We sent a 6-digit code to {{ phoneForm.value.phoneNumber }}
          </p>
        </div>

        <div class="card-content">
          <!-- Error Message -->
          <div *ngIf="error" class="alert alert-danger">
            {{ error }}
          </div>

          <form [formGroup]="otpForm" (ngSubmit)="onOtpSubmit()">
            <div class="form-group">
              <label for="otp">Verification Code</label>
              <input
                type="text"
                id="otp"
                class="form-control otp-input"
                formControlName="otp"
                placeholder="123456"
                maxlength="6"
                [disabled]="loading"
                autofocus
              />
              <small class="form-text text-muted">Enter the 6-digit code</small>
            </div>

            <div class="button-group">
              <button
                type="button"
                class="btn btn-secondary"
                (click)="goBackFromOtp()"
                [disabled]="loading"
              >
                Back
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                [disabled]="loading || otpForm.invalid"
              >
                <span *ngIf="!loading">Verify</span>
                <span *ngIf="loading" class="loading-content">
                  <span class="spinner"></span>
                  Verifying...
                </span>
              </button>
            </div>

            <div class="text-center mt-3">
              <button
                type="button"
                class="btn-link"
                (click)="resendOtp()"
                [disabled]="loading"
              >
                Didn't receive code? Resend
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Hidden reCAPTCHA container -->
      <div id="recaptcha-container"></div>

      <!-- Step 3: Signup Form -->
      <div *ngIf="currentStep === 'signup'" class="card">
        <div class="card-header text-center">
          <h2 class="card-title">Create Your Account</h2>
          <p class="card-description">
            We don't have an account with this number. Let's create one!
          </p>
        </div>

        <div class="card-content">
          <!-- Error Message -->
          <div
            *ngIf="error && currentStep === 'signup'"
            class="alert alert-danger"
          >
            {{ error }}
          </div>

          <form [formGroup]="signupForm" (ngSubmit)="onSignupSubmit()">
            <div class="form-group">
              <label for="fullName">Full Name</label>
              <input
                type="text"
                id="fullName"
                class="form-control"
                formControlName="fullName"
                placeholder="Jane Doe"
                [disabled]="loading"
              />
              <small
                class="text-danger"
                *ngIf="
                  signupForm.get('fullName')?.invalid &&
                  signupForm.get('fullName')?.touched
                "
              >
                Name must be at least 2 characters
              </small>
            </div>

            <div class="form-group">
              <label for="phone-display">Phone Number</label>
              <input
                type="text"
                id="phone-display"
                class="form-control"
                formControlName="phoneNumber"
                readonly
                [disabled]="loading"
              />
            </div>

            <div class="button-group">
              <button
                type="submit"
                class="btn btn-primary"
                [disabled]="loading || signupForm.invalid"
              >
                <span *ngIf="!loading">Create Account & Check In</span>
                <span *ngIf="loading" class="loading-content">
                  <span class="spinner"></span>
                  Creating account...
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Step 4: Complete -->
      <div *ngIf="currentStep === 'complete'" class="card">
        <div class="card-header text-center">
          <div class="icon-wrapper success">
            <i class="material-icons">check_circle</i>
          </div>
          <h2 class="card-title">You're All Set!</h2>
          <p class="card-description">
            {{ successMessage }}
          </p>
        </div>

        <div class="card-content">
          <!-- JPoints Earned (if any) -->
          <div *ngIf="pointsEarned > 0" class="jpoints-badge">
            <div class="jpoints-icon">
              <i class="material-icons">stars</i>
            </div>
            <p class="jpoints-text">
              <span>Thank you for your Check-In with Jujuja!</span>
              <span
                >You just earned
                <span class="jpoints-amount">{{ pointsEarned }} j-Points</span
                >!</span
              >
            </p>
            <p class="jpoints-subtitle">
              Please download the Jujuja App here to earn more j-Points
            </p>
          </div>

          <button class="btn btn-primary btn-block" (click)="goToApp()">
            <span *ngIf="pointsEarned > 0">View j-Points in App</span>
            <span *ngIf="pointsEarned === 0">Go to Jujuja App</span>
          </button>
          <button
            class="btn btn-secondary btn-block mt-2"
            (click)="downloadJujujaApp()"
          >
            Download Jujuja App
          </button>
        </div>
      </div>
    </div>
    <div class="copyright-footer">
      <div class="text-center mt-3">
        <img
          width="40"
          height="40"
          src="assets/icons/logo.svg"
          alt="Jujuja Logo"
        />
      </div>
      <p class="copyright-text">
        © {{ currentYear }} Jujuja. All rights reserved.
      </p>
    </div>
  </div>
</div>

<!-- Custom Alert Dialog -->
<app-alert-dialog
  [show]="showAlert"
  [config]="alertConfig"
  (confirmed)="onAlertConfirmed($event)"
  (closed)="showAlert = false"
></app-alert-dialog>
